import _ from 'lodash';

import {
  ADDRESS_COUNTRY_TYPE,
  BorrowerTypeLabel,
  CASH_OUT_FANNIE_FACTOR,
  CASH_OUT_FANNIE_MIN_AMOUNT,
  DEFAULT_COUNTRY,
  EmploymentStatusType,
  extendedIncomeTypes,
  FhaRefinancePurposeTypes,
  GovernmentRefinancePurposeByRefinancePurpose,
  GovernmentRefinancePurposeLoanTypes,
  GovernmentRefinancePurposeType,
  LOAN_APPLICATION_TAB_ENTITY_UI_MANAGE_ID_SEPARATOR,
  LoanPurposeType,
  LoanType,
  LoanTypePurposeType,
  MannerHeldType,
  RefinancePurposeType,
  SuffixType,
  VaRefinancePurposeTypes,
} from 'constants/loanApplication';
import { governmentRefinancePurposeOptions, refinancePurposeOptions } from 'constants/options';

import { getConditionalArray } from './data';

const PERCENTAGE = 100;

export const getPersonFullName = ({
  borrowerType,
  firstName,
  middleName,
  lastName,
  suffix,
  isWithSuffix,
  isWithMiddleName,
  isWithTypePrefix,
} = {}) =>
  [
    ...(isWithTypePrefix ? [BorrowerTypeLabel[borrowerType]] : []),
    firstName,
    ...(isWithMiddleName && !_.isNil(middleName) ? [middleName] : []),
    lastName,
    ...(isWithSuffix && !_.isNil(suffix) && suffix !== 'None' ? [suffix] : []),
  ]
    .filter(Boolean)
    .join(' ') ||
  BorrowerTypeLabel[borrowerType] ||
  '';

export const getPersonInitials = ({ firstName, lastName } = {}) => {
  if (!firstName && !lastName) {
    return '';
  }

  const getInitial = name => name.charAt(0);

  if (!lastName) {
    const words = firstName.split(' ').filter(Boolean);

    return words.length > 1
      ? `${getInitial(words[0])}${getInitial(words[1])}`
      : getInitial(words[0]);
  }

  return `${getInitial(firstName)}${getInitial(lastName)}`;
};

export const checkIsPurchaseFlow = loanPurpose => loanPurpose === LoanPurposeType.PURCHASE;

export const checkIsNotPurchaseFlow = loanPurpose =>
  loanPurpose && loanPurpose !== LoanPurposeType.PURCHASE;

export const checkIsRefinanceFlow = loanPurpose => loanPurpose === LoanPurposeType.REFINANCE;

export const checkIsRefinanceCashOutSubflow = ({ refinancePurpose }) =>
  [
    RefinancePurposeType.TAKE_CASH_OUT_OF_HOME,
    RefinancePurposeType.PAYOFF_CREDIT_CARDS,
    RefinancePurposeType.HOME_IMPROVEMENT,
  ].includes(refinancePurpose);

export const checkIsAdditionalLoanFlow = loanPurpose =>
  loanPurpose === LoanPurposeType.ADDITIONAL_LOAN;

export const checkIsChattelLoanPurpose = loanPurpose =>
  loanPurpose === LoanPurposeType.CHATTEL_LOAN;

export const checkIsLoanPurposeTypeBpl = type => type === LoanTypePurposeType.BPL;

export const checkIsHelocLoanType = loanType => loanType === LoanType.HELOC;

export const checkIsHeloanLoanType = loanType => loanType === LoanType.HELOAN;

export const checkIsUnsecuredLoanType = loanType => loanType === LoanType.PERSONAL;

export const checkIsChattelLoanType = loanType => loanType === LoanType.CHATTEL;

export const getEmploymentStatus = ({ incomeType, isCurrentlyEmployedHere, endDate }) => {
  if (extendedIncomeTypes.includes(incomeType)) {
    return !endDate && isCurrentlyEmployedHere
      ? EmploymentStatusType.CURRENT
      : EmploymentStatusType.PAST;
  }

  return !endDate ? EmploymentStatusType.CURRENT : EmploymentStatusType.PAST;
};

export const checkIsFannieCashOutAmount = ({ cashOutAmount, loanAmount }) =>
  cashOutAmount >= CASH_OUT_FANNIE_MIN_AMOUNT ||
  cashOutAmount >= loanAmount * CASH_OUT_FANNIE_FACTOR;

// Down payment $↔% conversion shared by every down-payment-editing surface (Loan Info tab,
// Lead form) so they stay mathematically consistent. Purchase price <= 0 returns null (blank)
// rather than NaN/Infinity from a divide-by-zero.
export const getDownPaymentPercent = ({ amount, purchasePrice }) => {
  const numericPurchasePrice = Number(purchasePrice);

  if (!numericPurchasePrice || _.isNil(amount)) {
    return null;
  }

  const numericAmount = Number(amount);

  return _.isNaN(numericAmount) ? null : (numericAmount / numericPurchasePrice) * PERCENTAGE;
};

export const getDownPaymentAmount = ({ percent, purchasePrice }) => {
  const numericPurchasePrice = Number(purchasePrice);

  if (!numericPurchasePrice || _.isNil(percent)) {
    return null;
  }

  const numericPercent = Number(percent);

  return _.isNaN(numericPercent)
    ? null
    : Math.round((numericPurchasePrice * numericPercent) / PERCENTAGE);
};

const getPartDependentPercentage = (
  isSecondPart,
  lastFilledStepIndex,
  firstPartLastStepIndex,
  secondPartStepsLength,
  firstPartStepsLength,
) =>
  isSecondPart
    ? (lastFilledStepIndex - firstPartLastStepIndex) / secondPartStepsLength
    : (lastFilledStepIndex + 1) / firstPartStepsLength;

// TODO: to think about removing that cause of new milestone functionality under FF EnableShowMilestoneBasedStatus
export const getProgressPercentage = ({ loanPurpose, percentDescription, lockDate }) => {
  if (!percentDescription) {
    return null;
  }

  if (lockDate) {
    return PERCENTAGE;
  }

  const { lastFilledStepIndex, lastPreQualificationStepIndex, stepsCount } = percentDescription;
  const isFirstStepPassed = !!loanPurpose;
  const isSecondPart = lastFilledStepIndex >= lastPreQualificationStepIndex;
  const firstPartStepsLength = lastPreQualificationStepIndex + 1;
  const secondPartStepsLength = stepsCount - firstPartStepsLength;
  const percentage = isFirstStepPassed
    ? getPartDependentPercentage(
        isSecondPart,
        lastFilledStepIndex,
        lastPreQualificationStepIndex,
        secondPartStepsLength,
        firstPartStepsLength,
      )
    : 0;

  return Math.floor(percentage * PERCENTAGE);
};

export const hasPropertyChanged = property => {
  if (_.isEmpty(property)) {
    return false;
  }

  const { initialValue, newValue } = property;

  const isValueRemainsEmpty = [initialValue, newValue].every(
    value => _.isNil(value) || value === '',
  );

  return initialValue !== newValue && !isValueRemainsEmpty;
};

const getViewDataChangesDependentFields = ({ dependentFields, pendingChangesData }) => {
  if (!pendingChangesData) {
    return false;
  }

  const fieldsToCheck = dependentFields?.length ? dependentFields : Object.keys(pendingChangesData);

  return _.some(fieldsToCheck, field => {
    const changedField = _.get(pendingChangesData, field);

    return hasPropertyChanged(changedField);
  });
};

const getChangesPath = (changesProps, index) =>
  `${changesProps.groupName}[${index}].${changesProps.fieldName}`;

export const getViewDataChanges = ({ pendingChangesData, configFields }) =>
  Object.values(configFields).reduce((acc, { name, changesProps }) => {
    if (!changesProps) {
      const changedField = _.get(pendingChangesData, name);
      const valueToSet = hasPropertyChanged(changedField);

      _.set(acc, name, valueToSet);

      return acc;
    }

    if (changesProps.groupName) {
      pendingChangesData[changesProps.groupName]?.forEach((data, index) => {
        const pathToSet = getChangesPath(changesProps, index);
        const valueToSet = getViewDataChangesDependentFields({
          dependentFields: changesProps.dependentFields,
          pendingChangesData: _.get(pendingChangesData, `${changesProps.groupName}[${index}]`),
        });
        _.set(acc, pathToSet, valueToSet);
      });

      return acc;
    }

    if (changesProps.dependentFields) {
      const pathToSet = name;
      const valueToSet = getViewDataChangesDependentFields({
        dependentFields: changesProps.dependentFields,
        pendingChangesData,
      });
      _.set(acc, pathToSet, valueToSet);

      return acc;
    }

    return acc;
  }, {});

export const getTabEntityUiManageId = ({ tabName, id }) =>
  [tabName, id].join(LOAN_APPLICATION_TAB_ENTITY_UI_MANAGE_ID_SEPARATOR);

export const getBorrowersOptions = borrowers =>
  borrowers.map(({ fullName, borrowerId, isSeparateApplication }) => ({
    name: fullName,
    value: borrowerId,
    isSeparateApplication: !!isSeparateApplication,
  }));

export const getNonBorrowerEntitiesOptions = nonBorrowerEntities =>
  nonBorrowerEntities.map(({ fullName, nonBorrowerEntityId }) => ({
    name: fullName,
    value: nonBorrowerEntityId,
    isNbe: true,
  }));

export const checkIsLoanTypeFHA = loanType => loanType === LoanType.FHA;

export const checkIsGovernmentRefinancePurposeVisible = (loanType, loanPurpose) =>
  checkIsRefinanceFlow(loanPurpose) && GovernmentRefinancePurposeLoanTypes.includes(loanType);

export const getGovernmentRefinancePurposeFilteredOptions = loanType => {
  if (loanType === LoanType.FHA) {
    return governmentRefinancePurposeOptions.filter(
      ({ value }) =>
        value !== GovernmentRefinancePurposeType.INTEREST_RATE_REDUCTION_REFINANCE_LOAN,
    );
  }

  if (loanType === LoanType.VA) {
    return governmentRefinancePurposeOptions.filter(
      ({ value }) => value !== GovernmentRefinancePurposeType.STREAM_LINE_WITHOUT_APPRAISAL,
    );
  }

  return governmentRefinancePurposeOptions;
};

export const getRefinancePurposeFilteredOptions = loanType => {
  const excludedValues = [
    ...getConditionalArray(loanType !== LoanType.FHA, FhaRefinancePurposeTypes),
    ...getConditionalArray(loanType !== LoanType.VA, VaRefinancePurposeTypes),
  ];

  return refinancePurposeOptions.filter(({ value }) => !excludedValues.includes(value));
};

export const getIsAllowedRefinancePurposeValue = (loanType, refinancePurpose) =>
  getRefinancePurposeFilteredOptions(loanType).some(({ value }) => value === refinancePurpose);

export const getDefaultRefinancePurposeOption = () =>
  refinancePurposeOptions.find(({ value }) => value === RefinancePurposeType.CONSOLIDATE_LOANS) ??
  null;

export const getDefaultGovernmentRefinancePurposeOption = () =>
  governmentRefinancePurposeOptions.find(
    ({ value }) => value === GovernmentRefinancePurposeType.FULL_DOCUMENTATION,
  ) ?? null;

export const getGovernmentRefinancePurposeOptionByRefinancePurpose = refinancePurpose => {
  const purposeValue = GovernmentRefinancePurposeByRefinancePurpose[refinancePurpose];

  return governmentRefinancePurposeOptions.find(({ value }) => value === purposeValue) ?? null;
};

export const getIsAllowedGovernmentRefinancePurposeValue = (
  loanType,
  governmentRefinancePurpose,
) => {
  const allowedValues = getGovernmentRefinancePurposeFilteredOptions(loanType).map(
    ({ value }) => value,
  );

  return allowedValues.includes(governmentRefinancePurpose);
};

export const checkIsPositiveRentalHistoryVisible = (loanType, loanPurpose) =>
  checkIsPurchaseFlow(loanPurpose) && checkIsLoanTypeFHA(loanType);

export const getBorrowerFullName = borrower => {
  if (!borrower) {
    return '';
  }

  const { firstName, middleName, lastName, suffix } = borrower;

  return (
    [
      firstName,
      middleName,
      lastName,
      ...getConditionalArray(!_.isNull(suffix) && suffix !== SuffixType.NONE, [suffix]),
    ]
      .filter(Boolean)
      .join(' ') || ''
  );
};

export const getAddressData = address => {
  const isOutsideUsa = address.country?.type === ADDRESS_COUNTRY_TYPE.NON_USA;
  const state = isOutsideUsa ? address.country?.state.state : address.country?.state?.abbreviation;

  return {
    street: address.street,
    unit: address.unit,
    city: address.city,
    county: address.county,
    state,
    zip: address.country?.zip,
    country: address.country?.name || DEFAULT_COUNTRY,
    isOutsideUsa,
    addressId: address.addressId,
  };
};

export const getIsBplCompanyNameVisible = ({ mannerHeld }) =>
  [MannerHeldType.LLC, MannerHeldType.CORPORATION, MannerHeldType.TRUST].includes(mannerHeld);

export const filterUsedOptions = ({ data, options, currentOption }) => {
  if (!data) {
    return options;
  }

  const usedValues = data.reduce((acc, { value }) => {
    if (value !== currentOption) {
      acc.push(value);
    }

    return acc;
  }, []);

  const filtered = options.filter(({ value }) => !usedValues.includes(value));

  return filtered;
};

export const getLiabilityOption = liability => ({
  name: liability.companyName || liability.creditorName || 'N/A',
  value: liability.liabilityId || liability.id,
  ...liability,
});

export const clearEntitySideDataFromEmptyData = entitySideData => {
  return Object.entries(entitySideData).reduce((acc, [key, value]) => {
    const isArray = _.isArray(value);
    const updatedData = isArray ? value.filter(data => !data.isNewlyAdded) : value;

    acc[key] = updatedData;

    return acc;
  }, {});
};

export const getInvalidTabsValidationMessage = tabs => {
  const verb = tabs.length === 1 ? 'has' : 'have';

  return `${tabs.join(', ')} ${verb} fields that need your attention`;
};
